import { api } from './api';
import { format } from 'date-fns';
import type { PitchInput, SummaryPitch, VideoPitch } from '@/types/pitch';

export interface MetricsConsolidatedResponse {
  success: boolean;
  data: {
    summary: {
      views_total: number;
      views_unique: number;
      plays_total: number;
      plays_unique: number;
      play_rate: number;
      cta_clicks: number;
      engagement: number;
      /** Um item por pitch, na ordem de `video.pitches` (vazio = vídeo sem pitch). */
      pitches: SummaryPitch[];
      conversions: number;
      conversion_rate: number;
      revenue: number;
      currency: string;
    };
    series: {
      views: Array<{
        date: string;
        views_total: number;
        views_unique: number;
        plays_total: number;
        plays_unique: number;
        play_rate: number;
      }>;
    };
    retention: {
      timeline: Array<{
        time_seconds: number;
        viewers_count: number;
        retention_pct: number;
      }>;
    };
    geo: {
      countries: Array<any>;
      states: Array<any>;
    };
    devices: {
      platforms: Array<any>;
      browsers: Array<any>;
    };
    references: Array<any>;
    traffic: {
      utm_source: Array<any>;
      utm_medium: Array<any>;
      utm_campaign: Array<any>;
      utm_term: Array<any>;
      utm_content: Array<any>;
    };
    trafficRetention: {
      utm_source: Array<any>;
      utm_medium: Array<any>;
      utm_campaign: Array<any>;
      utm_term: Array<any>;
      utm_content: Array<any>;
    };
    primeTimes?: {
      hourly: Array<{
        hour: number;
        unique_views: number;
        unique_plays: number;
        total_views: number;
        total_plays: number;
      }>;
      dayOfWeek: Array<{
        day: number;
        day_name: string;
        unique_views: number;
        unique_plays: number;
        total_views: number;
        total_plays: number;
      }>;
      hourlyByDay: Array<{
        day: number;
        hour: number;
        unique_views: number;
        unique_plays: number;
        total_views: number;
        total_plays: number;
        device_dominant: string;
        device_percentage: number;
        conversions: number;
        revenue: number;
      }>;
      peakHour: {
        hour: number;
        unique_views: number;
        unique_plays: number;
      };
      peakDay: {
        day: number;
        day_name: string;
        unique_views: number;
        unique_plays: number;
      };
    };
    video: {
      id: string;
      duration: number;
      /** Pitches do vídeo ordenados por `seconds` (até 5). */
      pitches: VideoPitch[];
      video_mp4?: string | null;
    };
  };
}

export interface VideoListItem {
  id_video: string;
  nomeVideo: string;
  duration: number;
  frame: string;
  createdAt: string;
  totalViews: number;
  videoMP4?: string;
}

export const metricsService = {
  /**
   * Busca métricas consolidadas de um vídeo
   */
  getConsolidatedMetrics: async (
    videoId: string,
    startDate: Date,
    endDate: Date
  ): Promise<MetricsConsolidatedResponse> => {
    const inicio = format(startDate, 'yyyy-MM-dd');
    const fim = format(endDate, 'yyyy-MM-dd');

    const response = await api.get<MetricsConsolidatedResponse>(
      `/metric/consolidated/${videoId}`,
      {
        params: { inicio, fim }
      }
    );

    return response;
  },

  /**
   * Busca lista de vídeos do usuário
   */
  getVideos: async (userId: string): Promise<VideoListItem[]> => {
    try {
      // Primeiro tenta a rota do dashboard (que retorna totalViews)
      const response = await api.get<{
        success: boolean;
        data: VideoListItem[];
      }>(`/videos/dashboard/${userId}`);

      if (response.data && Array.isArray(response.data)) {
        return response.data;
      }

      return [];
    } catch (error) {
      console.error('Erro ao buscar vídeos pela rota dashboard, tentando rota alternativa:', error);

      // Se falhar, tenta a rota antiga
      try {
        const response = await api.get<{
          response: VideoListItem[];
        }>(`/videos/get/${userId}`);

        if (response.response && Array.isArray(response.response)) {
          return response.response;
        }

        return [];
      } catch (fallbackError) {
        console.error('Erro ao buscar vídeos pela rota alternativa:', fallbackError);
        throw fallbackError;
      }
    }
  },

  /**
   * Substitui a lista inteira de pitches do vídeo (PUT /videos/:id/pitches).
   * Devolve a lista salva, ordenada por `seconds`. Erro de validação = HTTP 422
   * com `{ message }` (ver pitchSaveErrorMessage).
   */
  updatePitches: async (videoId: string, pitches: PitchInput[]): Promise<VideoPitch[]> => {
    const response = await api.put<{ pitches: VideoPitch[] }>(`/videos/${videoId}/pitches`, { pitches });
    return response?.pitches ?? [];
  },
};
